import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Image, Platform, ScrollView, StyleSheet, TouchableOpacity, View, Text } from 'react-native';
import { EventRegister } from 'react-native-event-listeners';
import { isTablet } from 'react-native-device-info';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { createStackNavigator } from '@react-navigation/stack';
import DashboardHeader from '../../components/DashboardHeader';
import ExploreComponent from '../../components/ExploreComponent';
import MenuComponent from '../../components/MenuComponent';
import ServiceDropdown from '../../components/ServiceDropdown';
import AppContext from '../../navigation/Context';
import * as MenuNavigation from '../../navigation/MenuNavigation';
import colors from '../../resources/colors';
import images from '../../resources/images';
import MenuScreen from '../menu/MenuScreen';
import { addDays, format, getDay, parse } from 'date-fns';
import MenuContext from '../../navigation/MenuContext';
import DataController from '../../controllers/DataController';
import FirebaseService from '../../controllers/FirebaseService';

const Today = ({ day, dayIndex }) => (
  <View>
    <DashboardHeader>{'Today'}</DashboardHeader>
    <ServiceDropdown
      day={day}
      blockTitle={'Today'}
      dayIndex={dayIndex}
      open={Platform.OS === 'web' || isTablet() ? true : false}
    />
  </View>
);

const Explore = () => (
  <View>
    <DashboardHeader>{'Explore'}</DashboardHeader>
    <ExploreComponent />
  </View>
);

const NextSunday = ({ day, dayIndex }) => (
  <View>
    <DashboardHeader>{'Next Sunday'}</DashboardHeader>
    <ServiceDropdown
      day={day}
      blockTitle={'Next Sunday'}
      dayIndex={dayIndex}
      open={Platform.OS === 'web' || isTablet() ? true : false}
    />
  </View>
);

const DashboardScreen = ({ route, navigation }) => {
  const context = useContext(AppContext);
  const menuContext = useContext(MenuContext);
  const { days } = context;

  __DEV__ && (days.length);
  
  const date = new Date().valueOf();
  const timezone = new Date().toLocaleString();
  const offset = new Date().getTimezoneOffset() * 60 * 1000;
  const time = new Date(new Date().valueOf() - offset);
 
  __DEV__ && console.log(date);
  __DEV__ && console.log(timezone);
  __DEV__ && console.log(offset);
  __DEV__ && console.log(time);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuWidth = 375;
  const menuOffset = useSharedValue(-menuWidth);
  const screenOffset = useSharedValue(0);
  const menuBackgroundOffset = useSharedValue(0);
  const currentDate = new Date();
  const dayOfWeek = getDay(new Date().valueOf());
  const daysToAdd = 7 - dayOfWeek;
  const nextSunday = addDays(new Date(), daysToAdd);
  const currentDayFormatted = DataController.formatDate(time);
  const nextSundayFormatted = DataController.formatDate(nextSunday);

  __DEV__ && console.log(currentDayFormatted);
  __DEV__ && console.log(nextSundayFormatted);

  const day = days.find((item) => item.Date === currentDayFormatted && (item.id && item.id.includes(currentDayFormatted)));
  const dayIndex = days.findIndex((item) => item.Date === currentDayFormatted && (item.id && item.id.includes(currentDayFormatted)));
  const sunday = days.find((item) => item.Date === nextSundayFormatted && (item.id && item.id.includes(nextSundayFormatted)));
  const sundayIndex = days.findIndex((item) => item.Date === nextSundayFormatted && (item.id && item.id.includes(nextSundayFormatted)));

  // console.log(day);
  // console.log(sunday);

  useEffect(() => {
    const getAppOpenBehaviour = async () => {
      const type = await DataController.getAppOpenBehaviour();

      if (type === 1) {
        !__DEV__ && FirebaseService.logEvent('day_viewed', { date: day.Date });
        navigation.navigate('Day', { day, dayIndex });
      }
    };

    getAppOpenBehaviour();
  }, []);

  const menuButtonPressed = useCallback(
    (value) => {
      menuOffset.value = withTiming(value ? 0 : -menuWidth, {
        duration: 250,
        easing: Easing.inOut(Easing.exp),
      });

      screenOffset.value = withTiming(value ? menuWidth : 0, {
        duration: 250,
        easing: Easing.inOut(Easing.exp),
      });

      setMenuOpen(value);
    },
    [menuOffset, menuWidth, screenOffset],
  );

  useEffect(() => {
    EventRegister.addEventListener('welcome/open-accounts', () => {
      navigation.navigate('Subscriptions');
    });


    return () => {
      EventRegister.removeEventListener('welcome/open-accounts');
    }
  }, [navigation]);

  useEffect(() => {
    const openMenu = () => {
      !__DEV__ && FirebaseService.logEvent('dashboard_menu_opened');
      EventRegister.emit('menu/toggle-menu');
    };

    navigation.setOptions({
      headerLeft: () => (
        <TouchableOpacity style={styles.buttonMenu} onPress={() => openMenu()}>
          <Image style={styles.menuIcon} source={images.Button_Hamburger} />
        </TouchableOpacity>
      ),
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <Image style={styles.menuIcon} source={images.Icon_Lectionary_Active} />
          <Text style={styles.headerTitle}>
            {'Lectionary'}
          </Text>
        </View>
      ),
      headerTitleAlign: 'center',
      headerStyle: {
        backgroundColor: colors.grey20,
        shadowRadius: 0,
        shadowOffset: {
          height: 0,
        },
        borderBottomWidth: 0.5,
        borderBottomColor: colors.grey50,
        height: Platform.OS === 'web' ? 44 : undefined,
      },
    });
  }, [menuButtonPressed, menuOpen, navigation]);

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContainer}
        indicatorStyle="black">
        <View style={styles.innerContainer}>
          <Explore />
          {day && <Today day={day} dayIndex={dayIndex} />}
          {sunday && <NextSunday day={sunday} dayIndex={sundayIndex} />}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.accentWhite,
    flex: 1,
    height: '100%',
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
  },
  scrollView: {
    flex: 1,
    minHeight: '100%',
  },
  scrollViewContainer: {
    paddingTop: 10,
    paddingHorizontal: isTablet() || Platform.OS === 'web' ? 32 : 16,
    paddingBottom: 54,
    alignItems: 'center',
  },
  innerContainer: {
    width: '100%',
    maxWidth: 1400,
  },
  headerTitleContainer: {
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  headerTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    color: colors.primary,
  },
});

export default DashboardScreen;
